// Product settings > Consumer Voice tab: the subreddit hints the audit
// searches first for this product, a Run CV Analysis button, and the
// product's analysis history (view / download). Hints are saved on their
// own endpoint (PATCH .../consumer-voice/settings) so the General form's
// save never clears them and vice versa.
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import axiosInstant from '../../../../api';
import {
  productConsumerVoiceEndpoint,
  productConsumerVoiceItemEndpoint,
  productConsumerVoiceSettingsEndpoint,
} from '../../../../api/endpoints';
import { ConsumerVoiceHistorySection } from '../../../../components/ConsumerVoice/ConsumerVoiceHistorySection';
import { ConsumerVoiceAudit } from '../../../../types/consumerVoice';
import { useNetworkScope } from '../../../../hooks/useNetworkScope';
import { useUser } from '../../../../hooks/useUser';
import { isInternal } from '../../../../utils';

interface Props {
  desireid: number;
  productTitle?: string | null;
  // The product's own (manufacturer) brand — the CV run is stamped with it.
  brandId: number | null;
  initialSubreddits?: string | null;
}

export function ProductConsumerVoicePanel({ desireid, productTitle, brandId, initialSubreddits }: Props) {
  const navigate = useNavigate();
  const { role } = useUser();
  const { basePath, inWorkspace } = useNetworkScope();
  const [value, setValue] = useState(initialSubreddits || '');
  const [saved, setSaved] = useState(initialSubreddits || '');
  // Search keyword hints for the demand sources (Google Trends / Keyword
  // Planner): what shoppers type when the marketing name is not it.
  const [keywords, setKeywords] = useState('');
  const [savedKeywords, setSavedKeywords] = useState('');
  const [brandKeywords, setBrandKeywords] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [starting, setStarting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [running, setRunning] = useState(false);
  // Brand-wide hints every product inherits (read-only here; edited on the
  // brand's form).
  const [brandHints, setBrandHints] = useState<{ name: string | null; hints: string | null }>({ name: null, hints: null });

  // The list payload may predate the column; fetch the saved value once.
  useEffect(() => {
    let cancelled = false;
    axiosInstant
      .get(`${productConsumerVoiceSettingsEndpoint(desireid)}${brandId ? `?brandId=${brandId}` : ''}`)
      .then((res) => {
        if (cancelled) return;
        const v = (res.data && res.data.cv_subreddits) || '';
        setValue(v);
        setSaved(v);
        const k = (res.data && res.data.cv_keywords) || '';
        setKeywords(k);
        setSavedKeywords(k);
        setBrandKeywords((res.data && res.data.brand_cv_keywords) || null);
        setBrandHints({
          name: (res.data && res.data.brand_name) || null,
          hints: (res.data && res.data.brand_cv_subreddits) || null,
        });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [desireid, brandId]);

  const dirty = value.trim() !== saved.trim() || keywords.trim() !== savedKeywords.trim();

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axiosInstant.patch(productConsumerVoiceSettingsEndpoint(desireid), {
        cv_subreddits: value,
        cv_keywords: keywords,
      });
      const v = (data && data.cv_subreddits) || '';
      setValue(v);
      setSaved(v);
      const k = (data && data.cv_keywords) || '';
      setKeywords(k);
      setSavedKeywords(k);
      toast.success(v || k ? 'Consumer Voice hints saved.' : 'Hints cleared — the defaults apply.');
    } catch {
      toast.error("Couldn't save the hints. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const run = async () => {
    if (!brandId) {
      toast.error('Set the product\'s brand in General first — the analysis is stamped with it.');
      return;
    }
    setStarting(true);
    try {
      if (dirty) await save();
      await axiosInstant.post(productConsumerVoiceEndpoint(desireid), { brandId });
      toast.success('Consumer Voice analysis started — it takes a few minutes.');
      setRunning(true);
      setRefreshKey((k) => k + 1);
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 409) toast('A Consumer Voice analysis is already running for this product.');
      else if (status === 429) toast.error("You've reached the hourly limit for Consumer Voice analyses.");
      else toast.error("Couldn't start the Consumer Voice analysis. Please try again.");
    } finally {
      setStarting(false);
    }
  };

  const createStrategy = (audit: ConsumerVoiceAudit) => {
    const params = new URLSearchParams({ desireid: String(desireid), cv: String(audit.id) });
    if (productTitle) params.set('title', productTitle);
    const path = inWorkspace || !isInternal(role) ? `${basePath}/opportunities/strategies/new` : '/proposal';
    navigate(`${path}?${params.toString()}`);
  };

  const listEndpoint = `${productConsumerVoiceEndpoint(desireid)}${brandId ? `?brandId=${brandId}` : ''}`;
  let runLabel = 'Run CV Analysis';
  if (starting) runLabel = 'Starting…';
  else if (running) runLabel = 'Analysis running…';

  return (
    <div className="flex flex-col gap-5 pt-4">
      <div>
        <label htmlFor="cv-subreddits" className="block text-[13px] font-semibold text-text-1 mb-1.5">
          Subreddits to search first
        </label>
        <input
          id="cv-subreddits"
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="e.g. headphones, audiophile, BudgetAudiophile"
          className="w-full px-3 py-2.5 rounded-lg border border-border-token bg-surface text-[13px] text-text-1 focus:outline-none focus:border-accent-text"
        />
        <p className="text-[12px] text-muted mt-1.5">
          Comma-separated, with or without the r/ prefix (up to 12). Leave empty to use the defaults for the
          product&apos;s category. The analysis also searches all of Reddit for the brand and product name.
        </p>
        {brandHints.hints ? (
          <p className="text-[12px] text-text-2 mt-1.5">
            Also inherited from {brandHints.name || 'the brand'}: <span className="font-semibold">{brandHints.hints}</span>
            <span className="text-muted"> (edit on the brand&apos;s settings)</span>
          </p>
        ) : null}
        <label htmlFor="cv-keywords" className="block text-[13px] font-semibold text-text-1 mb-1.5 mt-4">
          Search terms shoppers use
        </label>
        <input
          id="cv-keywords"
          type="text"
          value={keywords}
          onChange={(e) => setKeywords(e.target.value)}
          placeholder="e.g. soundlink flex, bose flex speaker"
          className="w-full px-3 py-2.5 rounded-lg border border-border-token bg-surface text-[13px] text-text-1 focus:outline-none focus:border-accent-text"
        />
        <p className="text-[12px] text-muted mt-1.5">
          Comma-separated (up to 10). Google Trends and Keyword Planner query these first; leave empty to derive
          terms from the brand and product name.
        </p>
        {brandKeywords ? (
          <p className="text-[12px] text-text-2 mt-1.5">
            Also inherited from {brandHints.name || 'the brand'}: <span className="font-semibold">{brandKeywords}</span>
          </p>
        ) : null}
        <div className="flex items-center gap-2 mt-2.5">
          <button
            type="button"
            onClick={save}
            disabled={!dirty || saving}
            className="text-[12px] font-semibold text-white bg-primary rounded-md px-3 py-1.5 cursor-pointer hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving…' : 'Save hints'}
          </button>
          <button
            type="button"
            onClick={run}
            disabled={starting || running}
            title={brandId ? 'Read what shoppers say and search about this product and score it' : 'Set the product brand first'}
            className="text-[12px] font-semibold text-text-2 border border-border-token rounded-md px-3 py-1.5 bg-transparent cursor-pointer hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {runLabel}
          </button>
        </div>
      </div>

      <ConsumerVoiceHistorySection
        title="Consumer Voice History"
        listEndpoint={listEndpoint}
        itemEndpoint={(id) => productConsumerVoiceItemEndpoint(desireid, id, brandId)}
        refreshKey={refreshKey}
        showChart
        onRowsChange={(rows) => setRunning(rows.some((r) => r.status === 'pending'))}
        onCreateStrategy={createStrategy}
      />
    </div>
  );
}
